import { supabase } from '../../shared/supabase/supabaseClient';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { applyCustomerPriceListRemote } from '../../priceLists/public-api';
import { withCustomerSyncOperationId, resolveCustomerSyncOperationId } from '../../sync/core/customerSyncOperationId';
import { applyCustomerViaRpc } from '../../sync/core/flushRpc';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import type { SyncQueueItem } from '../../sync/core/syncTypes';
import type { CustomerAccountListQuery, CustomerAccountRow, CustomerPortalTokenRow } from '../domain/customerTypes';
import {
  countCustomerAccountsFromWebSnapshot,
  fetchCustomerAccountsFromWebSnapshot,
  resolveCustomerDebtSinceFromCache,
} from './customerRepositoryShared';
import { upsertWebCustomerInCache } from './webCustomerSnapshotCache';

function toRemoteCustomerRow(input: {
  id: string;
  name: string;
  phone: string;
  cuit: string | null;
  creditLimit: number;
  balance?: number;
  blocked?: boolean;
  updatedAt: string;
  email?: string | null;
  address?: string | null;
  taxCategory?: string | null;
  notes?: string | null;
  paymentTermsDays?: number | null;
  priceListId?: string | null;
}) {
  return {
    id: input.id,
    name: input.name,
    phone: input.phone || null,
    cuit: input.cuit,
    credit_limit: input.creditLimit,
    balance: input.balance ?? 0,
    blocked: input.blocked ?? false,
    updated_at: input.updatedAt,
    email: input.email ?? null,
    address: input.address ?? null,
    tax_category: input.taxCategory ?? null,
    notes: input.notes ?? null,
    payment_terms_days: input.paymentTermsDays ?? null,
    price_list_id: input.priceListId ?? null,
  };
}

/** Web ram-only: push directo al RPC (sin cola). El servidor exige operationId. */
export async function pushCustomerProfileViaServer(
  operation: 'insert' | 'update',
  payload: Record<string, unknown>,
): Promise<void> {
  const operationId = resolveCustomerSyncOperationId(payload) ?? crypto.randomUUID();
  const stamped = { ...payload, operationId };
  const queueItem: SyncQueueItem = {
    id: crypto.randomUUID(),
    entity: 'fiado',
    operation,
    payload: stamped,
    status: 'pending',
    created_at: new Date().toISOString(),
  };
  const pushResult = await applyCustomerViaRpc(queueItem);
  if (!pushResult.ok) {
    const error = new Error(pushResult.error);
    captureOpsFailure({
      module: 'customers',
      op: 'customer.customer_write',
      error,
      ingestError: pushResult.error,
      applied: false,
      path: 'web-ram',
      extra: { operation },
    });
    throw error;
  }
}

export const countCustomerAccounts = async (query: CustomerAccountListQuery = {}): Promise<number> => {
  // ADR-017 Fase 4: SQLite local eliminado — leer de remote/cache.
  return countCustomerAccountsFromWebSnapshot(query);
};

export const fetchCustomerAccounts = async (opts?: {
  limit?: number;
  offset?: number;
  query?: CustomerAccountListQuery;
}): Promise<CustomerAccountRow[]> => {
  // ADR-017 Fase 4: SQLite local eliminado — leer de remote/cache.
  return fetchCustomerAccountsFromWebSnapshot(opts);
};

async function getCustomerAccountByIdRemote(customerId: string): Promise<CustomerAccountRow | null> {
  const { data, error } = await supabase
    .schema('customers')
    .from('customers')
    .select('id, name, phone, cuit, credit_limit, balance, blocked, updated_at, price_list_id')
    .eq('id', customerId)
    .maybeSingle();
  if (error || !data) return null;

  const { data: tokenRow } = await supabase
    .schema('customers')
    .from('customer_portal_tokens')
    .select(
      'customer_id, token, payment_link_url, qr_image_url, expected_amount, expires_at, last_payment_status, last_payment_amount, last_payment_at, last_payment_reference'
    )
    .eq('customer_id', customerId)
    .maybeSingle();

  const tokenByCustomerId = new Map<string, CustomerPortalTokenRow>();
  if (tokenRow) {
    tokenByCustomerId.set(String(tokenRow.customer_id), {
      customerId: String(tokenRow.customer_id),
      token: String(tokenRow.token ?? ''),
      paymentLinkUrl: tokenRow.payment_link_url ?? null,
      qrImageUrl: tokenRow.qr_image_url ?? null,
      expectedAmount: tokenRow.expected_amount ?? null,
      expiresAt: tokenRow.expires_at ?? null,
      lastPaymentStatus: tokenRow.last_payment_status ?? null,
      lastPaymentAmount: tokenRow.last_payment_amount ?? null,
      lastPaymentAt: tokenRow.last_payment_at ?? null,
      lastPaymentReference: tokenRow.last_payment_reference ?? null,
    });
  }

  const { data: reminderRow } = await supabase
    .schema('customers')
    .from('reminder_logs')
    .select('created_at')
    .eq('customer_id', customerId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  return {
    id: String(data.id),
    name: String(data.name ?? ''),
    phone: String(data.phone ?? ''),
    cuit: data.cuit ?? null,
    creditLimit: Number(data.credit_limit ?? 0),
    balance: Number(data.balance ?? 0),
    blocked: Boolean(data.blocked),
    updatedAt: data.updated_at ? String(data.updated_at) : null,
    // Referencia estable de mora desde el snapshot en RAM (null → fallback updatedAt).
    debtSince: resolveCustomerDebtSinceFromCache(String(data.id)),
    portalToken: tokenByCustomerId.get(String(data.id)) ?? null,
    lastReminderAt: reminderRow?.created_at ? String(reminderRow.created_at) : null,
    email: (data as any).email ?? null,
    address: (data as any).address ?? null,
    taxCategory: (data as any).tax_category ?? null,
    notes: (data as any).notes ?? null,
    paymentTermsDays: (data as any).payment_terms_days != null ? Number((data as any).payment_terms_days) : null,
    priceListId: (data as any).price_list_id ?? null,
  };
}

export const getCustomerAccountById = async (customerId: string): Promise<CustomerAccountRow | null> => {
  if (!customerId.trim()) return null;
  // ADR-017 Fase 4: SQLite local eliminado — leer de remote/cache.
  return getCustomerAccountByIdRemote(customerId);
};

/** Lookup liviano de nombres para módulos vecinos (cartera de cheques). */
export const listCustomerNamesByIds = async (ids: string[]): Promise<Map<string, string>> => {
  const unique = [...new Set(ids.filter((id) => id.trim() !== ''))];
  if (unique.length === 0) return new Map();
  const { data, error } = await supabase
    .schema('customers')
    .from('customers')
    .select('id, name')
    .in('id', unique);
  if (error || !data) return new Map();
  return new Map(data.map((row) => [String(row.id), String(row.name ?? '')]));
};

export const createCustomer = async (input: {
  name: string;
  phone: string;
  cuit?: string | null;
  creditLimit: number;
  email?: string | null;
  address?: string | null;
  taxCategory?: string | null;
  notes?: string | null;
  paymentTermsDays?: number | null;
  priceListId?: string | null;
}): Promise<string> => {
  const name = input.name.trim();
  if (!name) throw new Error('El nombre es obligatorio.');
  const phone = input.phone.trim();
  const limit = Number(input.creditLimit);
  if (Number.isNaN(limit) || limit < 0) throw new Error('Límite de crédito inválido.');

  const id = crypto.randomUUID();
  const now = new Date().toISOString();
  const cuit = input.cuit?.trim() || null;
  const email = input.email?.trim() || null;
  const address = input.address?.trim() || null;
  const taxCategory = input.taxCategory ?? 'consumidor_final';
  const notes = input.notes?.trim() || null;
  const paymentTermsDays = input.paymentTermsDays ?? 0;
  const priceListId = input.priceListId ?? null;

  await pushCustomerProfileViaServer('insert', {
          scope: 'profile',
          customerId: id,
          name,
          phone: phone || null,
          cuit,
          credit_limit: limit,
          balance: 0,
          blocked: false,
          updated_at: now,
          email,
          address,
          tax_category: taxCategory,
          notes,
          payment_terms_days: paymentTermsDays,
        });
        const branchId = useSessionStore.getState().currentBranchId;
        if (branchId && priceListId) {
          await applyCustomerPriceListRemote({
            customerId: id,
            branchId,
            operationId: crypto.randomUUID(),
            priceListId,
          });
        }
        upsertWebCustomerInCache(
          toRemoteCustomerRow({
            id,
            name,
            phone,
            cuit,
            creditLimit: limit,
            balance: 0,
            blocked: false,
            updatedAt: now,
            email,
            address,
            taxCategory,
            notes,
            paymentTermsDays,
            priceListId,
          }),
        );
        return id;
};

export const updateCustomerProfile = async (
  customerId: string,
  updates: {
    name?: string;
    phone?: string;
    cuit?: string | null;
    creditLimit?: number;
    email?: string | null;
    address?: string | null;
    taxCategory?: string | null;
    notes?: string | null;
    paymentTermsDays?: number | null;
    priceListId?: string | null;
  }
): Promise<void> => {
  let row: {
    name: string; phone: string; cuit: string | null; credit_limit: number;
    email: string | null; address: string | null; tax_category: string | null;
    notes: string | null; payment_terms_days: number | null; price_list_id: string | null;
  } | null = null;
  let webBalance = 0;
  let webBlocked = false;

  const remote = await getCustomerAccountByIdRemote(customerId);
        if (!remote) throw new Error('Cliente no encontrado');
        webBalance = remote.balance;
        webBlocked = remote.blocked;
        row = {
          name: remote.name,
          phone: remote.phone,
          cuit: remote.cuit ?? null,
          credit_limit: remote.creditLimit,
          email: remote.email ?? null,
          address: remote.address ?? null,
          tax_category: remote.taxCategory ?? null,
          notes: remote.notes ?? null,
          payment_terms_days: remote.paymentTermsDays ?? null,
          price_list_id: remote.priceListId ?? null,
        };
  if (!row) throw new Error('Cliente no encontrado');

  const name = updates.name !== undefined ? updates.name.trim() : row.name;
  if (!name) throw new Error('El nombre no puede quedar vacío.');
  const phone = updates.phone !== undefined ? updates.phone.trim() : row.phone;
  const cuit = updates.cuit !== undefined ? (updates.cuit?.trim() || null) : row.cuit;
  const creditLimit =
    updates.creditLimit !== undefined ? Number(updates.creditLimit) : row.credit_limit;
  if (Number.isNaN(creditLimit) || creditLimit < 0) throw new Error('Límite de crédito inválido.');
  const email = updates.email !== undefined ? (updates.email?.trim() || null) : row.email;
  const address = updates.address !== undefined ? (updates.address?.trim() || null) : row.address;
  const taxCategory = updates.taxCategory !== undefined ? updates.taxCategory : row.tax_category;
  const notes = updates.notes !== undefined ? (updates.notes?.trim() || null) : row.notes;
  const paymentTermsDays =
    updates.paymentTermsDays !== undefined ? (updates.paymentTermsDays ?? 0) : (row.payment_terms_days ?? 0);
  const priceListId =
    updates.priceListId !== undefined ? (updates.priceListId ?? null) : row.price_list_id;

  const now = new Date().toISOString();

  const payload: Record<string, unknown> = {
    scope: 'profile',
    customerId,
    updated_at: now,
  };
  if (updates.name !== undefined) payload.name = name;
  if (updates.phone !== undefined) payload.phone = phone || null;
  if (updates.cuit !== undefined) payload.cuit = cuit;
  if (updates.creditLimit !== undefined) payload.credit_limit = creditLimit;
  if (updates.email !== undefined) payload.email = email;
  if (updates.address !== undefined) payload.address = address;
  if (updates.taxCategory !== undefined) payload.tax_category = taxCategory;
  if (updates.notes !== undefined) payload.notes = notes;
  if (updates.paymentTermsDays !== undefined) payload.payment_terms_days = paymentTermsDays;
  if (updates.priceListId !== undefined) payload.price_list_id = priceListId;

  await pushCustomerProfileViaServer('update', payload);
        upsertWebCustomerInCache(
          toRemoteCustomerRow({
            id: customerId,
            name,
            phone,
            cuit,
            creditLimit,
            balance: webBalance,
            blocked: webBlocked,
            updatedAt: now,
            email,
            address,
            taxCategory,
            notes,
            paymentTermsDays,
            priceListId,
          }),
        );

  if (updates.priceListId !== undefined) {
    const branchId = useSessionStore.getState().currentBranchId;
    if (branchId) {
      await applyCustomerPriceListRemote({
        customerId,
        branchId,
        operationId: crypto.randomUUID(),
        priceListId,
      });
    }
  }

  // ADR-017 Fase 4: ramOnly siempre true — invalidate ya se hace en el path online
};

export const toggleCustomerBlockState = async (customerId: string) => {
  const now = new Date().toISOString();

  const customer = await getCustomerAccountByIdRemote(customerId);
        if (!customer) return;
        const nextBlocked = !customer.blocked;
        await pushCustomerProfileViaServer('update', {
          scope: 'profile',
          customerId,
          blocked: nextBlocked,
          updated_at: now,
        });
        upsertWebCustomerInCache(
          toRemoteCustomerRow({
            id: customerId,
            name: customer.name,
            phone: customer.phone ?? '',
            cuit: customer.cuit ?? null,
            creditLimit: customer.creditLimit,
            balance: customer.balance,
            blocked: nextBlocked,
            updatedAt: now,
            email: customer.email ?? null,
            address: customer.address ?? null,
            taxCategory: customer.taxCategory ?? null,
            notes: customer.notes ?? null,
            paymentTermsDays: customer.paymentTermsDays ?? null,
            priceListId: customer.priceListId ?? null,
          }),
        );
        return;
};
